// React side of the offline queue: subscription hooks and the week merge.
import type { WeekResponse } from "@zeiterfassung/shared";
import { useMemo, useSyncExternalStore } from "react";
import { type MergedWeek, mergeQueueIntoWeek } from "./merge-week";
import {
  type OfflineFailure,
  offlineQueue,
  type QueuedMutation,
  type ReplayStop,
} from "./offline-queue";

const EMPTY_ITEMS: readonly QueuedMutation[] = [];
const EMPTY_FAILURES: readonly OfflineFailure[] = [];

export function useQueuedMutations(): readonly QueuedMutation[] {
  return useSyncExternalStore(offlineQueue.subscribe, offlineQueue.items, () => EMPTY_ITEMS);
}

export function useOfflineFailures(): readonly OfflineFailure[] {
  return useSyncExternalStore(offlineQueue.subscribe, offlineQueue.failures, () => EMPTY_FAILURES);
}

export function useQueueReplaying(): boolean {
  return useSyncExternalStore(offlineQueue.subscribe, offlineQueue.isReplaying, () => false);
}

export function useReplayStop(): ReplayStop | null {
  return useSyncExternalStore(offlineQueue.subscribe, offlineQueue.lastStop, () => null);
}

// The week with the queued changes of the employee merged in (undefined while loading).
export function useMergedWeek(week: WeekResponse | undefined): MergedWeek | undefined {
  const queue = useQueuedMutations();
  return useMemo(() => (week ? mergeQueueIntoWeek(week, queue) : undefined), [week, queue]);
}
